import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchProjects, fetchProjectStats } from '../../store/slices/projectSlice';
import Layout from '../../components/Layout';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import StatusBadge from '../../components/StatusBadge';
import ProjectModal from './ProjectModal';
import { PROJECT_STATUS } from '../../utils/constants';
import { formatDateTime } from '../../utils/formatDate';

const ProjectsDashboard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const { projects, stats, pagination, loading } = useSelector((state) => state.projects);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState({ status: '', search: '' });
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  useEffect(() => {
    dispatch(fetchProjects({ page, ...filters }));
    dispatch(fetchProjectStats());
  }, [dispatch, page, filters]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters({ ...filters, [name]: value });
    setPage(1);
  };

  const isAdmin = user?.role === 'admin';

  return (
    <Layout>
      <div className="container-fluid">
        <div className="dashboard-header">
          <div>
            <h1>Projects</h1>
            <p className="mb-0 text-muted">Manage projects and track tasks within your team</p>
          </div>
          {isAdmin && (
            <button className="btn btn-primary" onClick={() => setIsCreateModalOpen(true)}>
              <i className="ti ti-plus me-1" />
              Create Project
            </button>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="stats-grid">
            <div className="stat-card">
              <h3>Total Projects</h3>
              <p className="stat-value">{stats.total}</p>
            </div>
            {Object.entries(stats.by_status || {}).map(([status, count]) => (
              <div key={status} className="stat-card">
                <h3><StatusBadge status={status} /></h3>
                <p className="stat-value">{count}</p>
              </div>
            ))}
          </div>
        )}

        {/* Filters */}
        <div className="card mb-3">
          <div className="card-body">
            <div className="row g-3">
              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Status</label>
                <select
                  name="status"
                  className="form-select"
                  value={filters.status}
                  onChange={handleFilterChange}
                >
                  <option value="">All Statuses</option>
                  {Object.values(PROJECT_STATUS).map((status) => (
                    <option key={status} value={status}>
                      {status.replace(/_/g, ' ')}
                    </option>
                  ))}
                </select>
              </div>

              <div className="col-sm-6 col-lg-3">
                <label className="form-label small mb-1">Search</label>
                <input
                  type="text"
                  name="search"
                  className="form-control"
                  placeholder="Search by project name"
                  value={filters.search}
                  onChange={handleFilterChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Projects Table */}
        <div className="card table-responsive">
          {loading ? (
            <Loading />
          ) : (
            <>
              <table className="table mb-0 text-nowrap table-hover">
                <thead className="table-light border-light">
                  <tr>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Members</th>
                    <th>Tasks</th>
                    <th>Created By</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {projects.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No projects found
                      </td>
                    </tr>
                  ) : (
                    projects.map((project) => (
                      <tr key={project._id} className="align-middle">
                        <td className="fw-medium">{project.name}</td>
                        <td><StatusBadge status={project.status} /></td>
                        <td>
                          <span className="badge bg-secondary">
                            {project.members?.length || 0} member{project.members?.length === 1 ? '' : 's'}
                          </span>
                        </td>
                        <td>
                          <span className="text-muted small">
                            {project.task_stats?.by_status?.completed || 0} / {project.task_stats?.total || 0} completed
                          </span>
                        </td>
                        <td>{project.created_by?.name || 'N/A'}</td>
                        <td>{formatDateTime(project.created_at)}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-outline-primary"
                            onClick={() => navigate(`/projects/${project._id}`)}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="p-3">
                <Pagination pagination={pagination} onPageChange={setPage} />
              </div>
            </>
          )}
        </div>
      </div>

      {isCreateModalOpen && (
        <ProjectModal
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={(project) => {
            setIsCreateModalOpen(false);
            dispatch(fetchProjects({ page, ...filters }));
            dispatch(fetchProjectStats());
            if (project?._id) {
              navigate(`/projects/${project._id}`);
            }
          }}
        />
      )}
    </Layout>
  );
};

export default ProjectsDashboard;
